import { dateLocale } from "@/lib/i18n";
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/common";
import { useSpecialDays } from "@/components/SpecialDays";
import { ClosuresPanel } from "@/components/ClosuresPanel";
import { api, todayIso } from "@/lib/api";
import { cn } from "@/lib/utils";

const COLORS = {
  ferie: "bg-primary/15 text-primary",
  permesso: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
  malattia: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
};
const WEEK = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const pad = (n) => String(n).padStart(2, "0");

export default function Calendario() {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [events, setEvents] = useState([]);
  const [people, setPeople] = useState([]);
  const [dept, setDept] = useState("all");
  const [rev, setRev] = useState(0);
  const sp = useSpecialDays(ym.y, ym.m, rev);

  useEffect(() => { api.get("/users/directory").then((r) => setPeople(r.data)); }, []);
  useEffect(() => { api.get("/admin/calendar", { params: { year: ym.y, month: ym.m } }).then((r) => setEvents(r.data)); }, [ym]);

  const depts = useMemo(() => [...new Set(people.map((p) => p.department).filter(Boolean))], [people]);
  const filtered = dept === "all" ? events : events.filter((e) => e.department === dept);
  const daysIn = new Date(ym.y, ym.m, 0).getDate();
  const lead = (new Date(ym.y, ym.m - 1, 1).getDay() + 6) % 7;
  const today = todayIso();
  const shift = (d) => setYm(({ y, m }) => { const t = m + d; return { y: y + Math.floor((t - 1) / 12), m: ((t - 1 + 12) % 12) + 1 }; });
  const title = new Date(ym.y, ym.m - 1, 1).toLocaleDateString(dateLocale(), { month: "long", year: "numeric" });

  return (
    <div data-testid="calendar-page">
      <PageHeader title="Calendario assenze" subtitle="Ferie e permessi approvati, malattie comunicate."
        actions={
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="w-48" data-testid="calendar-department-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i reparti</SelectItem>
              {depts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        } />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="calendar-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
        <h2 className="w-44 text-center text-lg font-semibold capitalize" data-testid="calendar-month-title">{title}</h2>
        <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="calendar-next-button"><ChevronRight className="h-4 w-4" /></Button>
        <div className="ml-auto flex flex-wrap gap-3 text-xs" data-testid="calendar-legend">
          {Object.keys(COLORS).map((k) => <span key={k} className={cn("rounded px-2 py-0.5 capitalize", COLORS[k])}>{k}</span>)}
          <span className="rounded bg-red-500/15 px-2 py-0.5 text-red-600 dark:text-red-400">Festività / chiusura</span>
          <span className="rounded bg-amber-400/25 px-2 py-0.5 text-amber-800 dark:text-amber-300">Ferie bloccate</span>
          <span className="rounded px-2 py-0.5 text-sky-800 ring-2 ring-inset ring-sky-400/70 dark:text-sky-300" data-testid="calendar-legend-overlap">Sovrapposizione</span>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-card">
        <div className="grid min-w-[760px] grid-cols-7">
          {WEEK.map((w) => <div key={w} className="border-b px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{w}</div>)}
          {Array.from({ length: lead }).map((_, i) => <div key={`l${i}`} className="min-h-[110px] border-b border-r bg-muted/30" />)}
          {Array.from({ length: daysIn }).map((_, i) => {
            const iso = `${ym.y}-${pad(ym.m)}-${pad(i + 1)}`;
            const dayEv = filtered.filter((e) => e.start <= iso && iso <= e.end);
            const wk = (lead + i) % 7 >= 5;
            const hol = sp.holiday(iso);
            const clo = sp.closure(iso);
            const blk = sp.blackout(iso);
            return (
              <div key={iso} className={cn("min-h-[110px] border-b border-r p-1.5", wk && "bg-muted/40", (hol || clo) && "bg-red-500/10", blk && !hol && !clo && "bg-amber-400/15", new Set(dayEv.map((x) => x.user_id)).size > 1 && "bg-sky-500/10 ring-2 ring-inset ring-sky-400/70")} data-testid={`calendar-day-${iso}`}>
                <div className="flex items-start justify-between gap-1">
                  <span className={cn("inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs", (hol || clo) && "font-semibold text-red-600 dark:text-red-400", iso === today && "bg-primary font-semibold text-primary-foreground")}>{i + 1}</span>
                  {(hol || clo || blk) && (
                    <span className={cn("truncate text-[10px] font-medium leading-6", hol || clo ? "text-red-600 dark:text-red-400" : "text-amber-700 dark:text-amber-300")} title={hol?.name || clo?.title || blk?.title} data-testid={`calendar-special-${iso}`}>
                      {hol?.name || clo?.title || blk?.title}
                    </span>
                  )}
                </div>
                <div className="mt-1 space-y-0.5">
                  {dayEv.slice(0, 3).map((e, j) => (
                    <div key={j} title={`${e.user_name} – ${e.type}${e.time ? " " + e.time : ""}`} className={cn("truncate rounded px-1.5 py-0.5 text-[11px] font-medium", COLORS[e.type])}>{e.user_name}</div>
                  ))}
                  {dayEv.length > 3 && <div className="px-1.5 text-[11px] text-muted-foreground">+{dayEv.length - 3} altri</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <ClosuresPanel onChange={() => setRev((r) => r + 1)} />
    </div>
  );
}
